/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { TOfferPreviewCustomData } from '../../../core/types';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { ServicesClientImpl } from '../../../core/impls';
import { SocuDataModelImpl } from '../../../core/socu/impls';
import { RootState } from '../../../store/root.reducers';
import {
    infoBannerHideBannerTip,
    infoBannerShowBannerTip,
} from '../../../store/videoplayerui/infobanner/infobanner.reducers';
import { DeviceFeatures } from '../../../core/device';
import { VideoManager } from '../../../core/videoplayer';

import './BannerTip.scss';

interface BannerTipProps {
    hideBannerTipAtProgramBoundary: boolean;
}

const BannerTip: FunctionComponent<BannerTipProps> = (props): JSX.Element => {
    const { hideBannerTipAtProgramBoundary } = props;
    const channel = VideoManager.getInstance().getPlayable()?.channel;
    const isSubscribed = (channel && channel.isSubscribed) ?? true;
    const [bannerTipText, setBannerTipText] = useState('');
    const isVisible: boolean = useSelector(
        (state: RootState) => state.infoBanner.isBannerTipVisible,
    );
    const playingSocu: boolean = SocuDataModelImpl.getInstance().isSocu();
    // TODO: Videomanager use should be eliminated
    const previewData: TOfferPreviewCustomData =
        VideoManager.getInstance().getMetadata().offerPreview || {};
    const enteringChannelNumberEnabled = useSelector(
        (state: RootState) => state.infoBanner.enteringChannelNumberEnabled,
    );
    const { isAvailableInSocu, contentPreview } = previewData;
    const dispatch = useDispatch();
    const isBannerTipVisible: boolean =
        DeviceFeatures.isSocuEnabled() &&
        isVisible &&
        !enteringChannelNumberEnabled &&
        !hideBannerTipAtProgramBoundary &&
        isSubscribed;

    /**
     * This useEffect will perform the following use cases of BannerTip:
     * - If SoCu content is playing, it will set the tip text: Press & hold OK/SELECT for live TV
     * - If playing live tv, it will check for the SoCu content availibility using OfferPreview data
     *   because at this point SocuDataModelImpl doesn't have any information available.
     * - If Start Over is available, it will set the tip text: Press & hold OK/SELECT to Start Over
     * - If CatchUp is available, it will set the tip text: Press & hold OK/SELECT for CatchUp
     */
    useEffect(() => {
        if (playingSocu) {
            setBannerTipText(
                Locale.getString(LocaleKeys.LIVE_TV_BANNER_TIP_PRESS_AND_HOLD_LIVE_TV_TEXT),
            );
            dispatch(infoBannerShowBannerTip());
        }

        /**
         * If playing live tv, checking for SoCu availibility and valid startTime and endTime string
         * to differentiate between Start Over and CatchUp program because at this point
         * no information is available in SocuDataModelImpl
         */
        if (
            !playingSocu &&
            isAvailableInSocu &&
            contentPreview &&
            contentPreview.startTime &&
            contentPreview.endTime
        ) {
            const services = new ServicesClientImpl();
            const currentTime = Date.now();
            const startTime = new Date(contentPreview.startTime).getTime();
            const endTime = new Date(contentPreview.endTime).getTime();
            const isStartOver: boolean = currentTime >= startTime && currentTime < endTime;
            if (isStartOver) {
                setBannerTipText(
                    `${Locale.getString(
                        LocaleKeys.LIVE_TV_BANNER_TIP_PRESS_AND_HOLD_TO_TEXT,
                    )}${services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER)}`,
                );
            } else {
                setBannerTipText(
                    `${Locale.getString(
                        LocaleKeys.LIVE_TV_BANNER_TIP_PRESS_AND_HOLD_TO_TEXT,
                    )}${services.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP)}`,
                );
            }

            dispatch(infoBannerShowBannerTip());
        }

        return () => {
            dispatch(infoBannerHideBannerTip());
        };
    }, [previewData]);

    return (
        <React.Fragment>
            {isBannerTipVisible ? <div className="banner-tip">{bannerTipText}</div> : null}
        </React.Fragment>
    );
};

BannerTip.propTypes = {
    hideBannerTipAtProgramBoundary: PropTypes.any.isRequired,
};

export default BannerTip;
